import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { Layout } from '../components/Layout';

interface Promotion {
  id: number;
  name: string;
  description: string | null;
  event_type: string;
  benefit_type: string;
  coupon_id: number | null;
  discount_value: number | null;
  discount_type: string | null;
  start_date: string;
  end_date: string;
  is_active: boolean;
  current_uses: number;
  max_uses: number | null;
  created_at: string;
}

interface PromotionsResponse {
  promotions: Promotion[];
  total: number;
  page: number;
  limit: number;
}

export const PromotionsPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [search, setSearch] = useState('');
  const [eventType, setEventType] = useState('');
  const [isActive, setIsActive] = useState<boolean | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState<Promotion | null>(null);
  const queryClient = useQueryClient();

  // 쿠폰 목록 조회 (항상 조회)
  const { data: couponsListData, isLoading: couponsLoading } = useQuery({
    queryKey: ['coupons-for-promotion'],
    queryFn: async () => {
      try {
        const response = await apiClient.get('/coupons', {
          params: {
            skip: 0,
            limit: 100
          }
        });
        console.log('쿠폰 목록 응답:', response.data);
        return response.data;
      } catch (error) {
        console.error('쿠폰 목록 조회 에러:', error);
        return { coupons: [], total: 0 };
      }
    },
  });

  // 활성화된 쿠폰만 필터링
  const availableCoupons = (couponsListData?.coupons || []).filter((c: any) => c.is_active);

  // 디버깅용
  React.useEffect(() => {
    console.log('availableCoupons:', availableCoupons);
    console.log('couponsLoading:', couponsLoading);
  }, [availableCoupons, couponsLoading]);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    event_type: 'login',
    benefit_type: 'coupon_issue',
    coupon_id: '',
    discount_value: '',
    discount_type: 'fixed',
    start_date: '',
    end_date: '',
    max_uses: '',
  });

  const { data: promotionsData, isLoading, error } = useQuery<PromotionsResponse>({
    queryKey: ['promotions', page, limit, search, eventType, isActive],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('skip', ((page - 1) * limit).toString());
      params.append('limit', limit.toString());
      if (search) params.append('search', search);
      if (eventType) params.append('event_type', eventType);
      if (isActive !== null) params.append('is_active', isActive.toString());

      const response = await apiClient.get<PromotionsResponse>(`/promotions?${params.toString()}`);
      return response.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await apiClient.post('/promotions', data);
      return response.data;
    },
    onSuccess: () => {
      alert('프로모션이 생성되었습니다.');
      setShowCreateModal(false);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
    },
    onError: (error: any) => {
      alert(error?.response?.data?.detail || '프로모션 생성에 실패했습니다.');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const response = await apiClient.patch(`/promotions/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      alert('프로모션이 수정되었습니다.');
      setEditingPromotion(null);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
    },
    onError: (error: any) => {
      alert(error?.response?.data?.detail || '프로모션 수정에 실패했습니다.');
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: number; is_active: boolean }) => {
      const response = await apiClient.patch(`/promotions/${id}`, { is_active });
      return response.data;
    },
    onSuccess: () => {
      alert('프로모션 상태가 변경되었습니다.');
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
    },
    onError: (error: any) => {
      alert(error?.response?.data?.detail || '상태 변경에 실패했습니다.');
    },
  });

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleReset = () => {
    setSearchInput('');
    setSearch('');
    setEventType('');
    setIsActive(null);
    setPage(1);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      event_type: 'login',
      benefit_type: 'coupon_issue',
      coupon_id: '',
      discount_value: '',
      discount_type: 'fixed',
      start_date: '',
      end_date: '',
      max_uses: '',
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const submitData: any = {
      name: formData.name,
      description: formData.description || null,
      event_type: formData.event_type,
      benefit_type: formData.benefit_type,
      start_date: new Date(formData.start_date).toISOString(),
      end_date: new Date(formData.end_date).toISOString(),
      max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
    };

    if (formData.benefit_type === 'coupon_issue' && formData.coupon_id) {
      submitData.coupon_id = parseInt(formData.coupon_id);
    } else if (formData.benefit_type === 'all_discount' || formData.benefit_type === 'vehicle_discount') {
      submitData.discount_value = parseFloat(formData.discount_value);
      submitData.discount_type = formData.discount_type;
    }

    if (editingPromotion) {
      updateMutation.mutate({ id: editingPromotion.id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleEdit = (promotion: Promotion) => {
    setEditingPromotion(promotion);
    setFormData({
      name: promotion.name,
      description: promotion.description || '',
      event_type: promotion.event_type,
      benefit_type: promotion.benefit_type,
      coupon_id: promotion.coupon_id?.toString() || '',
      discount_value: promotion.discount_value?.toString() || '',
      discount_type: promotion.discount_type || 'fixed',
      start_date: promotion.start_date.split('T')[0],
      end_date: promotion.end_date.split('T')[0],
      max_uses: promotion.max_uses?.toString() || '',
    });
    setShowCreateModal(true);
  };

  const handleToggleActive = (promotion: Promotion) => {
    if (window.confirm(`프로모션을 ${promotion.is_active ? '비활성화' : '활성화'} 하시겠습니까?`)) {
      toggleActiveMutation.mutate({ id: promotion.id, is_active: !promotion.is_active });
    }
  };

  const promotions = promotionsData?.promotions || [];
  const total = promotionsData?.total || 0;

  const eventTypeLabels: Record<string, string> = {
    login: '로그인',
    signup: '회원가입',
    first_reservation: '첫 예약',
    reservation: '예약',
    review_write: '리뷰 작성',
    other: '기타',
  };

  const benefitTypeLabels: Record<string, string> = {
    coupon_issue: '쿠폰 발급',
    vehicle_discount: '특정 차종 할인',
    all_discount: '전체 할인',
    point_issue: '포인트 지급',
    other: '기타',
  };

  return (
    <Layout>
      <div className="px-2 py-4">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-semibold text-gray-900">프로모션 관리</h1>
          <button
            onClick={() => {
              resetForm();
              setEditingPromotion(null);
              setShowCreateModal(true);
            }}
            className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700"
          >
            + 프로모션 생성
          </button>
        </div>

        {/* Search Form */}
        <div className="bg-white border border-slate-200 rounded-lg px-3 py-3 mb-4">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-700 mb-1">검색어</label>
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="block w-full px-2.5 py-1.5 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
                placeholder="프로모션 이름"
              />
            </div>

            <div className="w-32">
              <label className="block text-xs font-medium text-gray-700 mb-1">이벤트 종류</label>
              <select
                value={eventType}
                onChange={(e) => { setEventType(e.target.value); setPage(1); }}
                className="block w-full px-2.5 py-1.5 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
              >
                <option value="">전체</option>
                {Object.entries(eventTypeLabels).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>

            <div className="w-28">
              <label className="block text-xs font-medium text-gray-700 mb-1">활성 상태</label>
              <select
                value={isActive === null ? '' : isActive.toString()}
                onChange={(e) => {
                  setIsActive(e.target.value === '' ? null : e.target.value === 'true');
                  setPage(1);
                }}
                className="block w-full px-2.5 py-1.5 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
              >
                <option value="">전체</option>
                <option value="true">활성</option>
                <option value="false">비활성</option>
              </select>
            </div>

            <button onClick={handleSearch} className="w-16 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700">
              조회
            </button>
            <button onClick={handleReset} className="w-16 py-1.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50">
              초기화
            </button>
          </div>
        </div>

        {/* Results */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900">
              프로모션 목록
              {total > 0 && <span className="ml-2 text-sm font-normal text-gray-600">({total.toLocaleString()}건)</span>}
            </h2>
          </div>

          {isLoading ? (
            <div className="bg-white border border-slate-200 rounded-lg px-6 py-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600"></div>
              <p className="mt-3 text-slate-600 text-sm">로딩 중...</p>
            </div>
          ) : error ? (
            <div className="bg-white border border-slate-200 rounded-lg px-6 py-12 text-center">
              <p className="text-rose-600 text-sm">프로모션 조회에 실패했습니다.</p>
            </div>
          ) : promotions.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-lg px-6 py-12 text-center">
              <p className="text-slate-500 text-sm">프로모션이 없습니다.</p>
            </div>
          ) : (
            <>
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">이름</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">이벤트</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">혜택</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">시작일</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">종료일</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">사용량</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">상태</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">액션</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {promotions.map((promotion: Promotion) => (
                      <tr key={promotion.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                          {promotion.name}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                            {eventTypeLabels[promotion.event_type]}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                            {benefitTypeLabels[promotion.benefit_type]}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {new Date(promotion.start_date).toLocaleDateString('ko-KR')}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {new Date(promotion.end_date).toLocaleDateString('ko-KR')}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {promotion.current_uses} / {promotion.max_uses || '무제한'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            promotion.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {promotion.is_active ? '활성' : '비활성'}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <div className="flex gap-1.5">
                            <button
                              onClick={() => handleEdit(promotion)}
                              className="inline-flex items-center justify-center h-8 px-2.5 rounded-md border bg-white shadow-sm hover:bg-gray-50 transition-colors"
                              title="수정"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600">
                                <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z"></path>
                              </svg>
                            </button>
                            <button
                              onClick={() => handleToggleActive(promotion)}
                              className="inline-flex items-center justify-center h-8 px-2.5 rounded-md border bg-white shadow-sm hover:bg-gray-50 transition-colors"
                              title={promotion.is_active ? '비활성화' : '활성화'}
                            >
                              {promotion.is_active ? (
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-600">
                                  <circle cx="12" cy="12" r="10"></circle>
                                  <path d="m4.9 4.9 14.2 14.2"></path>
                                </svg>
                              ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-600">
                                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                </svg>
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="mt-4 flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600">
                    페이지 <span className="font-medium text-gray-900">{page}</span> / {Math.ceil(total / limit)}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">이전</button>
                  <button onClick={() => setPage(page + 1)} disabled={promotions.length < limit} className="px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">다음</button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Create/Edit Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-4">
                <h2 className="text-lg font-semibold text-gray-900">{editingPromotion ? '프로모션 수정' : '프로모션 생성'}</h2>
              </div>

              <form onSubmit={handleSubmit} className="p-5">
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">프로모션 이름 *</label>
                    <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full px-3 py-2 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm" />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">설명</label>
                    <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={2} className="w-full px-3 py-2 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm" />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">이벤트 종류 *</label>
                      <select required value={formData.event_type} onChange={(e) => setFormData({ ...formData, event_type: e.target.value })} className="w-full px-3 py-2 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm">
                        {Object.entries(eventTypeLabels).map(([key, label]) => (
                          <option key={key} value={key}>{label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">혜택 종류 *</label>
                      <select required value={formData.benefit_type} onChange={(e) => setFormData({ ...formData, benefit_type: e.target.value })} className="w-full px-3 py-2 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm">
                        {Object.entries(benefitTypeLabels).map(([key, label]) => (
                          <option key={key} value={key}>{label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {formData.benefit_type === 'coupon_issue' && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">
                        쿠폰 선택 * {couponsLoading && <span className="text-xs text-gray-500">(로딩 중...)</span>}
                      </label>
                      <select
                        required
                        value={formData.coupon_id}
                        onChange={(e) => {
                          console.log('선택된 쿠폰 ID:', e.target.value);
                          setFormData({ ...formData, coupon_id: e.target.value });
                        }}
                        className="w-full px-3 py-2 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
                        disabled={couponsLoading}
                      >
                        <option value="">쿠폰을 선택하세요 ({availableCoupons.length}개)</option>
                        {availableCoupons.map((coupon: any) => (
                          <option key={coupon.id} value={coupon.id}>
                            {coupon.name} ({coupon.code}) - {
                              coupon.discount_type === 'fixed'
                                ? `${Math.floor(coupon.discount_value).toLocaleString()}원`
                                : `${coupon.discount_value}%`
                            }
                          </option>
                        ))}
                      </select>
                      {!couponsLoading && availableCoupons.length === 0 && (
                        <p className="mt-1.5 text-xs text-red-600">
                          ⚠️ 사용 가능한 활성 쿠폰이 없습니다. 먼저 쿠폰 관리에서 쿠폰을 생성하고 활성화해주세요.
                        </p>
                      )}
                    </div>
                  )}

                  {(formData.benefit_type === 'all_discount' || formData.benefit_type === 'vehicle_discount') && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1.5">할인 유형</label>
                        <select value={formData.discount_type} onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })} className="w-full px-3 py-2 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm">
                          <option value="fixed">정액</option>
                          <option value="percentage">퍼센트</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1.5">할인 값</label>
                        <input type="number" step="0.01" value={formData.discount_value} onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })} className="w-full px-3 py-2 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm" />
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">시작일 *</label>
                      <input type="date" required value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} className="w-full px-3 py-2 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">종료일 *</label>
                      <input type="date" required value={formData.end_date} onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} className="w-full px-3 py-2 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">최대 사용 횟수</label>
                    <input type="number" value={formData.max_uses} onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })} placeholder="비워두면 무제한" className="w-full px-3 py-2 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm" />
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-gray-200">
                  <button type="button" onClick={() => { setShowCreateModal(false); setEditingPromotion(null); resetForm(); }} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
                    취소
                  </button>
                  <button type="submit" className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700">
                    {editingPromotion ? '수정' : '생성'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};
